// src/pages/auth/VerifyEmail.tsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useVerifyEmailMutation } from "../../app/api/authApi";

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifyEmail] = useVerifyEmailMutation();

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      setStatus("error");
      setErrorMessage("Verification token is missing from the URL");
      return;
    }

    const verify = async () => {
      try {
        await verifyEmail({ token }).unwrap();
        setStatus("success");

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 3000);
      } catch (err: any) {
        setStatus("error");
        setErrorMessage(
          err?.data?.error || "Invalid or expired verification token",
        );
      }
    };

    verify();
  }, [searchParams, verifyEmail, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Loading State */}
        {status === "loading" && (
          <>
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Loader2 size={32} className="text-green-600 animate-spin" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Verifying your email
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Please wait while we confirm your email address
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-center">
                <Loader2 size={24} className="text-green-500 animate-spin" />
                <span className="ml-3 text-gray-600">Processing...</span>
              </div>
            </div>
          </>
        )}

        {/* Success State */}
        {status === "success" && (
          <>
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 size={32} className="text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Email verified!
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Your email has been successfully verified
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
              <div className="text-center space-y-3 text-sm text-gray-600">
                <p className="flex items-center justify-center gap-2">
                  <CheckCircle2 size={16} className="text-green-600" />
                  <span>Your account is now active</span>
                </p>
                <p>You can now sign in and access all features.</p>
                <p className="text-xs text-gray-500">
                  Redirecting you to login in 3 seconds...
                </p>
              </div>

              <button
                onClick={() => navigate("/login")}
                className="w-full flex items-center justify-center gap-2 bg-green-500 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
              >
                Continue to Login
              </button>
            </div>
          </>
        )}

        {/* Error State */}
        {status === "error" && (
          <>
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircle size={32} className="text-red-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Verification failed
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                We couldn't verify your email address
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <XCircle size={16} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Verification Error</p>
                  <p className="mt-1">{errorMessage}</p>
                </div>
              </div>

              <div className="space-y-3 text-sm text-gray-600">
                <p>This verification link may have expired or is invalid.</p>
                <p className="font-medium text-gray-900">What you can do:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>Check if you used the most recent verification email</li>
                  <li>Request a new verification email</li>
                  <li>Contact support if the issue persists</li>
                </ul>
              </div>

              <div className="pt-4 border-t space-y-2">
                <Link
                  to="/login"
                  className="w-full flex items-center justify-center gap-2 bg-green-500 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
                >
                  Go to Login
                </Link>
                <Link
                  to="/register"
                  className="w-full flex items-center justify-center gap-2 bg-white text-gray-700 py-2.5 px-4 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
                >
                  Create New Account
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
